import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const {
  UNITS_LOADING_TIMEOUT_MS,
  shouldStopUnitsLoading,
} = require('@utils/unitsLoadingHelpers');
var userActions = require('@actions/userActions');

export function useUnitsRecalcObserver() {
  const dispatch = useDispatch();
  const unitsLoadingTimeoutRef = useRef(null);
  const farmingUnitSawInitRef = useRef(false);
  const fieldSawInitRef = useRef(false);
  const distributionsAtStartRef = useRef(null);

  const unitsRecalcLoading = useSelector((state) => state.units_recalc.loading);
  const distributions = useSelector(
    (state) => state.distribution_data.distributions,
  );
  const unitsRecalcHadFieldSelected = useSelector(
    (state) => state.units_recalc.hadFieldSelected,
  );
  const unitsRecalcHadFarmingUnitSelected = useSelector(
    (state) => state.units_recalc.hadFarmingUnitSelected,
  );
  const setUserAttributesFlowState = useSelector(
    (state) => state.authentication_data.set_user_attributes_flow?.state,
  );
  const farmingUnitLoadHistoryStatus = useSelector(
    (state) => state.distribution_data.farming_unit_load_history.status,
  );
  const fieldLoadHistoryStatus = useSelector(
    (state) => state.distribution_data.field_load_history.status,
  );

  const clearUnitsLoadingTimeout = useCallback(() => {
    if (unitsLoadingTimeoutRef.current) {
      clearTimeout(unitsLoadingTimeoutRef.current);
      unitsLoadingTimeoutRef.current = null;
    }
  }, []);

  const stopUnitsRecalc = useCallback(() => {
    dispatch(userActions.stop_units_recalc());
  }, [dispatch]);

  useEffect(() => {
    if (!unitsRecalcLoading) {
      farmingUnitSawInitRef.current = false;
      fieldSawInitRef.current = false;
      distributionsAtStartRef.current = null;
      clearUnitsLoadingTimeout();
      return;
    }

    // Snapshot the distributions reference at the moment loading starts.
    // Wave A fires against stale in-memory data before this reference changes;
    // Wave B fires after set_all_distributions updates it. We only latch sawInit
    // from Wave B so the spinner outlives the prefetch and covers the real reload.
    if (distributionsAtStartRef.current === null) {
      distributionsAtStartRef.current = distributions;
    }
    const distributionsRefreshed =
      distributions !== distributionsAtStartRef.current;

    if (distributionsRefreshed) {
      if (farmingUnitLoadHistoryStatus === 'init') {
        farmingUnitSawInitRef.current = true;
      }
      if (fieldLoadHistoryStatus === 'init') {
        fieldSawInitRef.current = true;
      }
    }

    if (
      shouldStopUnitsLoading({
        unitsLoading: unitsRecalcLoading,
        setUserAttributesFlowState,
        farmingUnitLoadHistoryStatus,
        fieldLoadHistoryStatus,
        hadFieldSelected: unitsRecalcHadFieldSelected,
        hadFarmingUnitSelected: unitsRecalcHadFarmingUnitSelected,
        farmingUnitSawInit: farmingUnitSawInitRef.current,
        fieldSawInit: fieldSawInitRef.current,
      })
    ) {
      clearUnitsLoadingTimeout();
      stopUnitsRecalc();
    }
  }, [
    unitsRecalcLoading,
    distributions,
    setUserAttributesFlowState,
    farmingUnitLoadHistoryStatus,
    fieldLoadHistoryStatus,
    unitsRecalcHadFieldSelected,
    unitsRecalcHadFarmingUnitSelected,
    stopUnitsRecalc,
    clearUnitsLoadingTimeout,
  ]);

  useEffect(() => {
    if (!unitsRecalcLoading) {
      return;
    }

    clearUnitsLoadingTimeout();
    unitsLoadingTimeoutRef.current = setTimeout(() => {
      unitsLoadingTimeoutRef.current = null;
      stopUnitsRecalc();
    }, UNITS_LOADING_TIMEOUT_MS);

    return clearUnitsLoadingTimeout;
  }, [unitsRecalcLoading, stopUnitsRecalc, clearUnitsLoadingTimeout]);

  useEffect(() => () => clearUnitsLoadingTimeout(), [clearUnitsLoadingTimeout]);
}
